import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const chapters = [
  {
    n: "01",
    title: "Weightless power",
    audience: "For athletes",
    body:
      "Water resistance is 800× denser than air. Every stroke builds strength while your joints float free — the fastest recovery-safe way to move heavy without the load.",
    img:
      "https://images.unsplash.com/photo-1530549387789-4c1017266635?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    align: "left",
  },
  {
    n: "02",
    title: "Gentle by design",
    audience: "For rehab & seniors",
    body:
      "Buoyancy removes up to 90% of body weight, so healing bodies can move confidently. Guided low-impact routines rebuild mobility, balance and calm.",
    img:
      "https://images.pexels.com/photos/8028648/pexels-photo-8028648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    align: "right",
  },
  {
    n: "03",
    title: "Fluid metrics",
    audience: "For the data-driven",
    body:
      "Track laps, stroke rate, calories and recovery in real time. Aqua Fit Coach turns every session in the water into a story you can actually measure and beat.",
    img:
      "https://images.pexels.com/photos/56837/swimmers-swimming-race-competition-56837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    align: "left",
  },
];

const Chapter = ({ c }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const right = c.align === "right";

  return (
    <div
      ref={ref}
      data-testid={`chapter-${c.n}`}
      className="grid items-center gap-10 md:grid-cols-12 md:gap-16"
    >
      <div className={`md:col-span-6 ${right ? "md:order-2" : ""}`}>
        <div className="overflow-hidden rounded-2xl border border-textMuted/20">
          <motion.img
            style={{ y }}
            src={c.img}
            alt={c.title}
            className="h-[400px] w-full scale-110 object-cover md:h-[520px]"
          />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`md:col-span-6 ${right ? "md:order-1 md:pr-10" : "md:pl-10"}`}
      >
        <span className="font-serif-italic text-7xl text-aqua md:text-8xl">{c.n}</span>
        <p className="mt-4 text-xs font-600 uppercase tracking-[0.3em] text-textMuted">{c.audience}</p>
        <h3 className="mt-3 font-display text-4xl font-800 tracking-tight text-textMain md:text-6xl">
          {c.title}
        </h3>
        <p className="mt-5 max-w-md text-base leading-relaxed text-textMuted md:text-lg">{c.body}</p>
      </motion.div>
    </div>
  );
};

export const Manifesto = () => {
  return (
    <section id="manifesto" data-testid="manifesto" className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mb-24 max-w-2xl"
      >
        <p className="mb-4 text-xs font-600 uppercase tracking-[0.3em] text-aqua">The manifesto</p>
        <h2 className="font-display text-4xl font-800 leading-[0.95] tracking-tight text-textMain md:text-6xl">
          Three reasons the water changes everything.
        </h2>
      </motion.div>

      <div className="space-y-28 md:space-y-40">
        {chapters.map((c) => (
          <Chapter key={c.n} c={c} />
        ))}
      </div>
    </section>
  );
};
